<template>
  <div class="lp-root">

    <!-- NAV -->
    <nav class="lp-nav">
      <div class="lp-container lp-nav-inner">
        <a href="/" class="lp-logo-link">
          <img src="/ciobai_logo_long.png" alt="Ciobai" class="lp-logo-img" />
        </a>
        <div class="lp-nav-links">
          <a href="#come-funziona" class="lp-nav-link">How it works</a>
          <a href="#prezzi" class="lp-nav-link">Pricing</a>
          <a href="#faq" class="lp-nav-link">FAQ</a>
          <a href="#contatto" class="lp-btn lp-btn-primary lp-btn-nav">Let's talk</a>
        </div>
      </div>
    </nav>

    <!-- HERO -->
    <section class="lp-hero">
      <div class="lp-container">
        <div class="lp-hero-grid">
          <div>
            <div class="lp-hero-badge">
              <span class="lp-pulse-dot"></span>
              <span>SMBs worldwide · 10–50 employees</span>
            </div>

            <h1 class="lp-h-display">
              Still answering every WhatsApp, email and <em>call by hand?</em>
            </h1>

            <p class="lp-lede" style="margin-top: 28px;">
              Automate WhatsApp, email, and calls. Save 14 hours a week.
            </p>

            <div class="lp-hero-ctas">
              <a href="https://wa.me/41784227091?text=Hi%2C%20I%20saw%20Ciobai%20and%20want%20to%20know%20more" target="_blank" class="lp-btn lp-btn-primary lp-btn-lg">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.5 14.4c-.3-.2-1.8-.9-2-1-.3-.1-.5-.2-.7.2-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.2-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2.1-.4 0-.5-.1-.2-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.5.3-.7.3-1.4.2-1.5-.1-.1-.3-.2-.5-.3zM12 2C6.5 2 2 6.5 2 12c0 1.9.5 3.7 1.5 5.3L2 22l4.8-1.5c1.5.8 3.3 1.3 5.2 1.3 5.5 0 10-4.5 10-10S17.5 2 12 2z"/></svg>
                Message me on WhatsApp
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
              </a>
            </div>

            <div class="lp-hero-trust">
              <span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6 9 17l-5-5"/></svg>
                Free consultation
              </span>
              <span class="lp-divider-dot">·</span>
              <span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6 9 17l-5-5"/></svg>
                No commitment
              </span>
              <span class="lp-divider-dot">·</span>
              <span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6 9 17l-5-5"/></svg>
                Data in Switzerland
              </span>
            </div>

            <div class="lp-hero-credit">— Alexandru Ciobanu, Founder</div>
          </div>

          <!-- Before/After card replacing founder photo -->
          <aside class="lp-hero-scenario-card">
            <div class="lp-hero-scenario-header">
              <span class="lp-eyebrow">Medical clinic · 8 staff</span>
            </div>
            <div class="lp-hero-scenario-before">
              <h4>Before</h4>
              <p>Receptionist spends 3 hours a day answering phones for bookings. Patients call outside hours and you lose appointments.</p>
            </div>
            <div class="lp-hero-scenario-after">
              <h4>After · with Ciobai</h4>
              <p>An AI agent answers calls and WhatsApp 24/7, books directly in Google Calendar, sends automatic confirmations.</p>
              <span class="lp-scenario-metric">–14 hours per week</span>
            </div>
          </aside>
        </div>
      </div>
    </section>

    <!-- PARTNERS -->
    <section class="lp-partners">
      <div class="lp-container">
        <div class="lp-partners-label">
          <span class="lp-eyebrow">Already working with European SMBs</span>
        </div>
        <div class="lp-partners-grid">
          <div v-for="logo in partnerLogos" :key="logo.alt" class="lp-partner-logo-wrap">
            <img :src="logo.src" :alt="logo.alt" class="lp-partner-logo" />
          </div>
        </div>
      </div>
    </section>

    <!-- SCENARIOS -->
    <section class="lp-section">
      <div class="lp-container-narrow">
        <div class="lp-section-head">
          <span class="lp-eyebrow">Real scenarios</span>
          <h2 class="lp-h-section">Here's your week, <em>before and after.</em></h2>
          <p class="lp-lede">No abstract promises. Three concrete situations we see every day in SMBs.</p>
        </div>

        <div class="lp-scenarios">
          <div class="lp-scenario" v-for="sc in scenarios" :key="sc.num">
            <div>
              <div class="lp-scenario-num">{{ sc.num }}</div>
              <div class="lp-scenario-kind">{{ sc.kind }}</div>
              <div class="lp-scenario-tag">{{ sc.tag }}</div>
            </div>
            <div class="lp-scenario-col lp-before">
              <h4>Before</h4>
              <p>{{ sc.before }}</p>
            </div>
            <div class="lp-scenario-col lp-after">
              <h4>After · with Ciobai</h4>
              <p>{{ sc.after }}</p>
              <span class="lp-scenario-metric">{{ sc.metric }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CAPABILITIES -->
    <section class="lp-section lp-section-alt" id="come-funziona">
      <div class="lp-container-narrow">
        <div class="lp-section-head">
          <span class="lp-eyebrow">What we build</span>
          <h2 class="lp-h-section">Four types of systems, <em>always tailored.</em></h2>
          <p class="lp-lede">We don't sell a closed product. Every system is built on your real processes, integrated with the tools you already use.</p>
        </div>

        <div class="lp-capabilities">
          <div class="lp-cap" v-for="cap in capabilities" :key="cap.title">
            <div class="lp-cap-icon" v-html="cap.icon"></div>
            <h3>{{ cap.title }}</h3>
            <p>{{ cap.desc }}</p>
            <div class="lp-cap-examples">
              <ul>
                <li v-for="ex in cap.examples" :key="ex">→ {{ ex }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- PROCESS -->
    <section class="lp-section" id="come-funziona-passi">
      <div class="lp-container-narrow">
        <div class="lp-section-head">
          <span class="lp-eyebrow">How we work</span>
          <h2 class="lp-h-section">Three steps. <em>One commitment at a time.</em></h2>
          <p class="lp-lede">No giant contract to sign blindly. We start with a conversation, you decide if the next step makes sense.</p>
        </div>

        <div class="lp-process-steps">
          <div class="lp-step" v-for="step in steps" :key="step.num">
            <div class="lp-step-num">{{ step.num }}</div>
            <h3>{{ step.title }}</h3>
            <p>{{ step.desc }}</p>
            <div class="lp-step-time">{{ step.time }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- TESTIMONIALS -->
    <section class="lp-section lp-section-alt">
      <div class="lp-container-narrow">
        <div class="lp-section-head">
          <span class="lp-eyebrow">What people say</span>
          <h2 class="lp-h-section">Real people. <em>Real companies.</em></h2>
        </div>

        <div class="lp-quotes">
          <div class="lp-quote" v-for="q in testimonials" :key="q.name">
            <div class="lp-quote-mark">"</div>
            <p class="lp-quote-body">{{ q.body }}</p>
            <div class="lp-quote-attr">
              <div class="lp-quote-avatar">{{ q.initials }}</div>
              <div>
                <div class="lp-quote-name">{{ q.name }}</div>
                <div class="lp-quote-meta">{{ q.meta }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- PRICING -->
    <section class="lp-section" id="prezzi">
      <div class="lp-container-narrow">
        <div class="lp-section-head">
          <span class="lp-eyebrow">Pricing</span>
          <h2 class="lp-h-section">Transparent. <em>No surprises.</em></h2>
          <p class="lp-lede">Every project is different, but these are real ranges. If you don't fit, I'll tell you at the first call — I won't waste your time.</p>
        </div>

        <div class="lp-pricing">
          <div class="lp-price-cell">
            <div class="lp-price-label">Initial setup</div>
            <div class="lp-price-amount">
              <span class="lp-currency">$</span>300<span class="lp-per"> — 900</span>
            </div>
            <p class="lp-price-desc">One-time. Includes analysis, prototype, integration with your tools, training, go-live.</p>
            <ul class="lp-price-list">
              <li>Process analysis and agent design</li>
              <li>Integration with 2–4 existing systems</li>
              <li>Training on your data and documents</li>
              <li>Testing with your team and assisted go-live</li>
            </ul>
          </div>

          <div class="lp-price-cell">
            <div class="lp-price-label">Monthly management</div>
            <div class="lp-price-amount">
              <span class="lp-currency">$</span>100<span class="lp-per"> / month</span>
            </div>
            <p class="lp-price-desc">Starting from. Includes hosting, monitoring, updates, direct support. First month included in setup.</p>
            <ul class="lp-price-list">
              <li>Hosting in Switzerland, encrypted data</li>
              <li>Active conversation monitoring</li>
              <li>Continuous model optimization</li>
              <li>Response within 4h during business hours</li>
            </ul>
          </div>
        </div>
        <p class="lp-price-note">The first call and initial prototype are free. You only pay if we go to production.</p>
      </div>
    </section>

    <!-- FAQ -->
    <section class="lp-section lp-section-alt" id="faq">
      <div class="lp-container-narrow">
        <div class="lp-section-head">
          <span class="lp-eyebrow">Frequently asked</span>
          <h2 class="lp-h-section">What we're <em>always asked.</em></h2>
        </div>

        <div class="lp-faq">
          <details class="lp-faq-item" v-for="faq in faqs" :key="faq.q">
            <summary class="lp-faq-q">
              <span>{{ faq.q }}</span>
              <span class="lp-faq-toggle">+</span>
            </summary>
            <p class="lp-faq-a">{{ faq.a }}</p>
          </details>
        </div>
      </div>
    </section>

    <!-- CTA / FORM -->
    <section class="lp-cta-section" id="contatto">
      <div class="lp-container">
        <div class="lp-cta-grid">
          <div>
            <span class="lp-eyebrow">Let's start</span>
            <h2 class="lp-h-section lp-h-cta" style="margin-top: 20px;">A 20-minute conversation. <em>Zero commitment.</em></h2>
            <p class="lp-lede lp-lede-cta" style="margin-top: 20px;">Tell me your most broken process. If I can help you I'll tell you clearly, with numbers. If I can't, I'll point you in the right direction.</p>

            <ul class="lp-cta-expectations">
              <li><span class="lp-num">01</span><span>No one will call you at 8pm to "close the deal". I'll get back to you within 24h to schedule the call.</span></li>
              <li><span class="lp-num">02</span><span>At the first meeting we only talk about your processes. No pitch, no slides.</span></li>
              <li><span class="lp-num">03</span><span>If it makes sense to continue, I'll propose a concrete prototype. If not, we part as friends.</span></li>
            </ul>
          </div>

          <div>
            <!-- Success state -->
            <div v-if="submitted" class="lp-cta-form lp-success-state">
              <div class="lp-success-check">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M20 6 9 17l-5-5"/></svg>
              </div>
              <h3 class="lp-success-title">Request received.</h3>
              <p class="lp-success-sub">I'll reach out personally within 24 hours. — Alexandru</p>
            </div>

            <!-- Form -->
            <form v-else class="lp-cta-form" @submit.prevent="handleSubmit">
              <div class="lp-form-row">
                <label>Full name</label>
                <input v-model="form.name" type="text" name="name" required placeholder="John Smith" />
              </div>
              <div class="lp-form-row">
                <label>Email</label>
                <input v-model="form.email" type="email" name="email" required placeholder="john@company.com" />
              </div>
              <div class="lp-form-row">
                <label>Company <span class="lp-optional">(optional)</span></label>
                <input v-model="form.company" type="text" name="company" placeholder="E.g. Acme Inc." />
              </div>
              <div class="lp-form-row">
                <label>Your most painful process <span class="lp-optional">(optional)</span></label>
                <textarea v-model="form.process" name="process" placeholder="E.g. Our receptionist spends too much time answering booking calls…"></textarea>
              </div>
              <button type="submit" class="lp-form-submit" :disabled="sending">
                {{ sending ? 'Sending…' : 'Book the 20-minute call' }}
              </button>
              <p class="lp-form-foot">Zero spam. Zero cold calls. Response within 24h.</p>
            </form>

            <a href="https://wa.me/41784227091?text=Hi%2C%20I%20want%20to%20book%20a%20call%20with%20Ciobai" target="_blank" class="lp-cta-alt">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#25D366"><path d="M17.5 14.4c-.3-.2-1.8-.9-2-1-.3-.1-.5-.2-.7.2-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.2-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2.1-.4 0-.5-.1-.2-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.5.3-.7.3-1.4.2-1.5-.1-.1-.3-.2-.5-.3zM12 2C6.5 2 2 6.5 2 12c0 1.9.5 3.7 1.5 5.3L2 22l4.8-1.5c1.5.8 3.3 1.3 5.2 1.3 5.5 0 10-4.5 10-10S17.5 2 12 2z"/></svg>
              <span>Or message me directly on WhatsApp →</span>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- CHAT DEMO WIDGET -->
    <div class="lp-chat-widget">
      <transition name="lp-chat-slide">
        <div v-if="chatOpen" class="lp-chat-panel">
          <div class="lp-chat-header">
            <div class="lp-chat-header-info">
              <div class="lp-chat-avatar">AI</div>
              <div>
                <div class="lp-chat-title">Ciobai AI</div>
                <div class="lp-chat-subtitle">
                  <span class="lp-pulse-dot" style="width:6px;height:6px;"></span>
                  Online now
                </div>
              </div>
            </div>
            <button class="lp-chat-close-btn" @click="chatOpen = false" aria-label="Close">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
          </div>

          <div class="lp-chat-messages" ref="messagesEl">
            <div
              v-for="(msg, i) in chatMessages"
              :key="i"
              :class="['lp-chat-msg', msg.role === 'user' ? 'lp-chat-msg-user' : 'lp-chat-msg-ai']"
            >
              <div class="lp-chat-bubble" v-html="formatMessage(msg.content)"></div>
            </div>
            <div v-if="chatLoading" class="lp-chat-msg lp-chat-msg-ai">
              <div class="lp-chat-bubble lp-chat-typing">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>

          <!-- Suggested messages -->
          <div v-if="showSuggestions" class="lp-chat-suggestions">
            <button
              v-for="s in suggestions"
              :key="s"
              class="lp-chat-chip"
              @click="sendSuggestion(s)"
            >{{ s }}</button>
          </div>

          <form class="lp-chat-input-row" @submit.prevent="sendChatMessage">
            <input
              v-model="chatInput"
              type="text"
              class="lp-chat-input"
              placeholder="Type a message…"
              :disabled="chatLoading"
              ref="chatInputEl"
            />
            <button type="submit" class="lp-chat-send" :disabled="chatLoading || !chatInput.trim()">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M22 2 11 13M22 2 15 22 11 13 2 9l20-7z"/></svg>
            </button>
          </form>

          <div class="lp-chat-footer-note">Ciobai · Switzerland · Available 24/7</div>
        </div>
      </transition>

      <button class="lp-chat-toggle" @click="toggleChat" :class="{ 'lp-chat-toggle-open': chatOpen }">
        <transition name="lp-icon-swap" mode="out-in">
          <span v-if="!chatOpen" key="open" class="lp-chat-toggle-inner">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
            <span>Chat with Gio Bot 24/7</span>
          </span>
          <span v-else key="close" class="lp-chat-toggle-inner">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </span>
        </transition>
      </button>
    </div>

    <!-- FOOTER -->
    <footer class="lp-footer">
      <div class="lp-container lp-footer-inner">
        <div>© 2026 Ciobai — Switzerland</div>
        <div class="lp-footer-links">
          <router-link to="/contact" class="lp-nav-link">Contact</router-link>
          <a href="https://wa.me/41784227091" target="_blank" class="lp-nav-link">WhatsApp</a>
        </div>
      </div>
    </footer>

  </div>
</template>

<script setup>
import { ref, nextTick, computed } from 'vue'

const submitted = ref(false)
const sending = ref(false)
const form = ref({ name: '', email: '', company: '', process: '' })

// ── Chat demo ──
const CHAT_API_URL = 'https://chatbot.ciobai.ch/chat'

const chatOpen = ref(false)
const chatInput = ref('')
const chatLoading = ref(false)
const messagesEl = ref(null)
const chatInputEl = ref(null)
const chatSessionId = ref(null)
const chatMessages = ref([
  {
    role: 'assistant',
    content: 'Hi! I\'m Ciobai\'s AI assistant. Tell me: what\'s the most repetitive or slow process in your business? I\'ll show you how we could automate it.'
  }
])

const suggestions = [
  'I run a medical clinic',
  'How much does it cost?',
  'How does the free prototype work?',
]
const showSuggestions = computed(() =>
  chatMessages.value.length <= 1 && !chatLoading.value
)

function formatMessage(text) {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br>')
}

async function sendSuggestion(text) {
  chatInput.value = text
  await sendChatMessage()
}

async function scrollToBottom() {
  await nextTick()
  if (messagesEl.value) {
    messagesEl.value.scrollTop = messagesEl.value.scrollHeight
  }
}

async function toggleChat() {
  chatOpen.value = !chatOpen.value
  if (chatOpen.value) {
    window.plausible?.('use_chatbot', { props: { use_chatbot: 'open' } })
    await nextTick()
    chatInputEl.value?.focus()
    scrollToBottom()
  }
}

async function sendChatMessage() {
  const text = chatInput.value.trim()
  if (!text || chatLoading.value) return

  chatMessages.value.push({ role: 'user', content: text })
  chatInput.value = ''
  chatLoading.value = true
  scrollToBottom()
  if (chatMessages.value.filter(m => m.role === 'user').length === 1) {
    window.plausible?.('send_chatbot', { props: { send_chatbot: 'first_message' } })
  }

  try {
    const res = await fetch(CHAT_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: chatSessionId.value,
        messages: chatMessages.value.map(m => ({ role: m.role, content: m.content }))
      })
    })
    if (!res.ok) throw new Error('Network error')
    const data = await res.json()
    if (data.session_id) chatSessionId.value = data.session_id
    chatMessages.value.push({ role: 'assistant', content: data.message || '…' })
  } catch (e) {
    chatMessages.value.push({
      role: 'assistant',
      content: 'Sorry, I encountered an error. Please try again in a moment or contact me directly on WhatsApp.'
    })
  } finally {
    chatLoading.value = false
    scrollToBottom()
    await nextTick()
    chatInputEl.value?.focus()
  }
}

async function handleSubmit() {
  sending.value = true
  const payload = {
    tracking_code: 'AVElLWEEFS1qC3341qy6mQ',
    url: window.location.href,
    form_name: 'AI Infrastructure Landing English',
    form_id: 'ai-infrastruttura-form-en',
    form_action: window.location.href,
    form_data: { ...form.value },
    field_names: ['name', 'email', 'company', 'process'],
    referrer: document.referrer || null,
    user_agent: navigator.userAgent || null
  }
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        'https://panettone-zeta.vercel.app/analytics/api/c',
        new Blob([JSON.stringify(payload)], { type: 'application/json' })
      )
    } else {
      await fetch('https://panettone-zeta.vercel.app/analytics/api/c', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      })
    }
  } catch (_) { /* silent */ }
  sending.value = false
  submitted.value = true
  window.plausible?.('send_form', { props: { send_form: 'ai-infrastruttura-en' } })
}

const partnerLogos = [
  { src: require('../assets/img/zeth-logo-small.png'), alt: 'ZETH' },
  { src: require('../assets/img/drytech.png'), alt: 'Drytech' },
  { src: require('../assets/img/tisalabs_logo.png'), alt: 'TisaLabs' },
  { src: require('../assets/img/adwater.png'), alt: 'Adwater' },
  { src: require('../assets/img/cagnavin.png'), alt: 'Cagnavin' },
  { src: require('../assets/img/Logo_fernando_curti.jpg'), alt: 'Fernando Curti' },
]

const scenarios = [
  {
    num: 'SC / 01',
    kind: 'Medical clinic',
    tag: '8 staff · Europe',
    before: 'Receptionist spends 3 hours a day answering the phone for bookings. Patients call outside hours and you lose appointments. Confirmations written by hand.',
    after: 'An AI agent answers calls and WhatsApp 24/7, books directly in Google Calendar, sends automatic confirmations and reminders. Receptionist handles only complex cases.',
    metric: '–14 hours per week'
  },
  {
    num: 'SC / 02',
    kind: 'Mechanic workshop',
    tag: '22 employees · Europe',
    before: 'Quotes done manually in Excel, 30 minutes each. Clients wait 2-3 days for a response. Many choose the faster competitor.',
    after: 'Client sends photo on WhatsApp. AI classifies the job, extracts data from your system, sends a ballpark quote in 4 minutes. Job closure tripled.',
    metric: 'From 2 days to 4 minutes'
  },
  {
    num: 'SC / 03',
    kind: 'Consulting agency',
    tag: '14 consultants · Europe',
    before: 'Team wastes time on internal coordination emails, meeting notes, client summaries. Billable hours burned on admin work.',
    after: 'AI workflow listens to calls, writes meeting notes, prepares follow-ups, updates CRM. Consultants focus on billable work again.',
    metric: '+18% billable hours'
  }
]

const capabilities = [
  {
    title: 'Conversational agent',
    desc: 'Replies to WhatsApp, email, website chat. Understands multiple languages and knows your business. Handles bookings, quotes, FAQs, lead qualification.',
    examples: ['WhatsApp Business', 'Email Outlook / Gmail', 'Website live chat'],
    icon: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>`
  },
  {
    title: 'AI voice receptionist',
    desc: 'Answers calls with natural voice. Takes appointments, routes calls, qualifies callers. Perfect when the office is closed or the line is busy.',
    examples: ['Dedicated phone line', 'Calendar integration', 'Call transcription'],
    icon: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>`
  },
  {
    title: 'Process automation',
    desc: 'Workflows connecting your tools and doing repetitive work. Invoices, notes, reports, data entry, follow-ups. Monitored, logged, under your control.',
    examples: ['ERP / Accounting software', 'Google Workspace / Microsoft 365', 'CRM (HubSpot, Pipedrive)'],
    icon: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M4 6h16M4 12h16M4 18h16"/></svg>`
  },
  {
    title: 'Dashboard and reporting',
    desc: 'See what the AI does, how much time it\'s saving, where it gets stuck. Monthly reports in plain language, not a technical dashboard. Total transparency.',
    examples: ['Conversations handled', 'Estimated hours saved', 'Logs and human review'],
    icon: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 3v18h18M7 14l4-4 4 4 5-5"/></svg>`
  }
]

const steps = [
  {
    num: '01',
    title: 'Conversation',
    desc: '20 minutes on phone or video. You tell me about your processes, I figure out if I can really help or if you should go elsewhere.',
    time: 'Free · this week'
  },
  {
    num: '02',
    title: 'Prototype',
    desc: 'If it makes sense, I build a working prototype on your real case. You test it with your actual data before committing to anything.',
    time: 'Free · 5–7 days'
  },
  {
    num: '03',
    title: 'Live',
    desc: 'If the prototype convinces you, we integrate with your systems. Go live with dedicated support. If not, we part ways and you learned something.',
    time: '2–3 weeks · then support'
  }
]

const testimonials = [
  {
    body: 'I was worried it would be one of those AI systems that don\'t actually work. Instead, after two weeks our WhatsApp inbox was empty and I finally got to sleep at night.',
    initials: 'L',
    name: 'Laura B.',
    meta: 'Dental clinic · Europe'
  },
  {
    body: 'No fancy slides, no vague promises. He showed me a working prototype before even talking about price. That\'s why I signed.',
    initials: 'A',
    name: 'Andrea C.',
    meta: 'Auto repair · Europe'
  }
]

const faqs = [
  {
    q: 'Why is the prototype free? What\'s the catch?',
    a: 'No catch. We only work with companies where we can really make a difference, and the only way to know is to try. The prototype cost is my investment to see if it makes sense to work together. If after the prototype you\'re not convinced, you owe me nothing.'
  },
  {
    q: 'Where do my data go?',
    a: 'Swiss servers. Nothing is sent in clear to OpenAI or Google without your explicit consent. We sign a DPA and you remain the data owner. For regulated sectors (medical, legal, financial) we use on-premise models when required.'
  },
  {
    q: 'What if the AI makes a mistake with an important client?',
    a: 'Good question. That\'s why every system has "safety zones": on ambiguous requests, complaints, or sensitive topics, the AI escalates directly to a human. You see every conversation in the dashboard and can intervene. No system goes live without a week of supervised testing.'
  },
  {
    q: 'Are we too small? Is 10 people enough?',
    a: '10 people is perfect. Below 5 employees it usually makes sense to wait. Above 50 the project type changes. The ideal window for us is 10–50 employees with at least one role dedicated to handling client requests.'
  },
  {
    q: 'How long does it take from start to finish?',
    a: 'From first call to prototype: about 7 days. From prototype to live production: 2–3 weeks on average. So you\'re operational in about a month.'
  },
  {
    q: 'Do you speak English / German / French?',
    a: 'I speak English and Italian natively. The systems we build handle English, German, French, Italian, and Spanish without issues — useful for multilingual clients.'
  }
]

defineOptions({ name: 'AiAgentInfrastructureEnglishPage' })
</script>

<style scoped>
/* ── Reset scoped to this page ── */
.lp-root {
  --bg: #ffffff;
  --bg-card: #ffffff;
  --ink: #13110e;
  --ink-2: #413d36;
  --ink-3: #6b665c;
  --ink-4: #a7a196;
  --line: #ececec;
  --line-2: #dddddd;
  --accent: #0a3b5c;
  --accent-ink: #ffffff;
  --accent-soft: #e3ecf3;
  --serif: "Satoshi", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
  --sans: "Satoshi", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
  --mono: "Geist Mono", ui-monospace, SFMono-Regular, Menlo, monospace;

  background: var(--bg);
  color: var(--ink);
  font-family: var(--sans);
  font-size: 16px;
  line-height: 1.55;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
}

/* Layout */
.lp-container { max-width: 1160px; margin: 0 auto; padding: 0 32px; }
.lp-container-narrow { max-width: 880px; margin: 0 auto; padding: 0 32px; }
@media (max-width: 640px) {
  .lp-container, .lp-container-narrow { padding: 0 20px; }
}

/* Nav */
.lp-nav {
  position: sticky; top: 0; z-index: 50;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: saturate(180%) blur(16px);
  -webkit-backdrop-filter: saturate(180%) blur(16px);
  border-bottom: 1px solid var(--line);
}
.lp-nav-inner {
  display: flex; align-items: center; justify-content: space-between;
  height: 64px;
}
.lp-logo-link { display: flex; align-items: center; }
.lp-logo-img { height: 22px; width: auto; display: block; }
.lp-nav-links { display: flex; align-items: center; gap: 28px; }
.lp-nav-link {
  font-family: var(--sans);
  font-size: 14px; color: var(--ink-2);
  text-decoration: none;
  transition: color .15s ease;
}
.lp-nav-link:hover { color: var(--ink); }
@media (max-width: 720px) { .lp-nav-links .lp-nav-link:not(.lp-btn) { display: none; } }

/* Buttons */
.lp-btn {
  display: inline-flex; align-items: center; justify-content: center;
  gap: 8px;
  padding: 0 20px; height: 44px;
  font-family: var(--sans);
  font-size: 14px; font-weight: 500;
  border-radius: 10px;
  border: 1px solid transparent;
  transition: all .18s ease;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
}
.lp-btn-primary {
  background: var(--accent); color: var(--accent-ink);
  border-color: var(--accent);
}
.lp-btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -12px rgba(10, 59, 92, 0.5);
}
.lp-btn-ghost {
  background: transparent; color: var(--ink);
  border-color: var(--line-2);
}
.lp-btn-ghost:hover { background: var(--bg-card); border-color: var(--ink-3); }
.lp-btn-lg { height: 52px; padding: 0 28px; font-size: 15px; border-radius: 12px; }
.lp-btn-nav { height: 38px; padding: 0 16px; font-size: 13px; }

/* Eyebrow */
.lp-eyebrow {
  font-family: var(--mono);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-3);
}

/* Typography */
.lp-h-display {
  font-family: var(--sans);
  font-weight: 600;
  font-size: clamp(36px, 5.8vw, 76px);
  line-height: 1.04;
  letter-spacing: -0.035em;
  color: var(--ink);
  margin: 0;
}
.lp-h-display em { font-style: normal; color: var(--accent); font-weight: 600; }
.lp-h-section {
  font-family: var(--sans);
  font-weight: 600;
  font-size: clamp(28px, 3.8vw, 46px);
  line-height: 1.12;
  letter-spacing: -0.03em;
  color: var(--ink);
  margin: 0;
  padding-bottom: 2px;
}
.lp-h-section em { font-style: normal; color: var(--accent); font-weight: 600; }
.lp-lede {
  font-family: var(--sans);
  font-size: clamp(16px, 1.5vw, 19px);
  line-height: 1.55;
  color: var(--ink-2);
  max-width: 52ch;
}

/* Pulse dot */
.lp-pulse-dot {
  width: 7px; height: 7px; border-radius: 999px;
  background: var(--accent);
  position: relative;
  flex-shrink: 0;
}
.lp-pulse-dot::after {
  content: ''; position: absolute; inset: -4px;
  border-radius: 999px;
  background: var(--accent); opacity: 0.25;
  animation: lp-pulse 2s ease-out infinite;
}
@keyframes lp-pulse {
  0% { transform: scale(0.6); opacity: 0.4; }
  100% { transform: scale(1.6); opacity: 0; }
}

/* Hero */
.lp-hero { padding: 80px 0 96px; }
.lp-hero-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
  align-items: start;
}
@media (min-width: 960px) {
  .lp-hero-grid { grid-template-columns: 1.4fr 1fr; gap: 72px; }
}
.lp-hero-badge {
  display: inline-flex; align-items: center; gap: 10px;
  padding: 6px 12px;
  border: 1px solid var(--line-2);
  background: var(--bg-card);
  border-radius: 999px;
  font-family: var(--sans);
  font-size: 13px;
  color: var(--ink-2);
  margin-bottom: 28px;
}
.lp-hero-ctas {
  display: flex; flex-wrap: wrap; gap: 12px;
  margin-top: 36px;
}
.lp-hero-trust {
  display: flex; flex-wrap: wrap; gap: 16px;
  margin-top: 22px;
  font-family: var(--sans);
  font-size: 13px; color: var(--ink-3);
  align-items: center;
}
.lp-hero-trust span { display: inline-flex; align-items: center; gap: 6px; }
.lp-divider-dot { color: var(--ink-4); }

/* Hero founder credit */
.lp-hero-credit {
  font-family: var(--sans);
  font-size: 13px;
  color: var(--ink-3);
  margin-top: 16px;
}

/* Hero scenario card */
.lp-hero-scenario-card {
  background: var(--bg-card);
  border: 1px solid var(--line);
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.lp-hero-scenario-header {
  padding: 20px 24px 0;
}
.lp-hero-scenario-before,
.lp-hero-scenario-after {
  padding: 16px 24px;
}
.lp-hero-scenario-before {
  border-bottom: 1px solid var(--line);
}
.lp-hero-scenario-before h4,
.lp-hero-scenario-after h4 {
  font-family: var(--mono); font-size: 11px; letter-spacing: 0.14em;
  text-transform: uppercase; color: var(--ink-3);
  margin: 0 0 8px; font-weight: 500;
}
.lp-hero-scenario-before p {
  margin: 0; font-size: 14px; color: var(--ink-3); line-height: 1.55;
}
.lp-hero-scenario-after {
  padding-left: 22px;
  border-left: 3px solid var(--accent);
  margin: 0 24px 24px;
  padding: 16px 20px;
  border-radius: 0 12px 12px 0;
  background: rgba(10, 59, 92, 0.03);
}
.lp-hero-scenario-after p {
  margin: 0; font-size: 14px; color: var(--ink); line-height: 1.55;
}

/* Partners */
.lp-partners {
  padding: 56px 0;
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
}
.lp-partners-label { text-align: center; margin-bottom: 32px; }
.lp-partners-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px 16px;
  align-items: center;
  justify-items: center;
}
@media (min-width: 640px) { .lp-partners-grid { grid-template-columns: repeat(3, 1fr); } }
@media (min-width: 960px) { .lp-partners-grid { grid-template-columns: repeat(6, 1fr); } }
.lp-partner-logo-wrap {
  display: flex; align-items: center; justify-content: center;
  height: 48px;
  opacity: 0.6;
  transition: opacity .2s ease;
}
.lp-partner-logo-wrap:hover { opacity: 1; }
.lp-partner-logo { max-height: 36px; max-width: 120px; width: auto; object-fit: contain; }

/* Sections */
.lp-section { padding: 104px 0; }
.lp-section-head { margin-bottom: 56px; max-width: 720px; }
.lp-section-head .lp-eyebrow { margin-bottom: 20px; display: block; }
.lp-section-head .lp-h-section { margin-bottom: 20px; }
.lp-section-alt {
  background: #f8f8f8;
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
}

/* Scenarios */
.lp-scenarios { display: grid; grid-template-columns: 1fr; gap: 0; }
.lp-scenario {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  padding: 40px 0;
  border-top: 1px solid var(--line);
}
.lp-scenario:last-child { border-bottom: 1px solid var(--line); }
@media (min-width: 800px) {
  .lp-scenario { grid-template-columns: 180px 1fr 1fr; gap: 48px; align-items: start; }
}
.lp-scenario-num {
  font-family: var(--mono); font-size: 12px;
  color: var(--ink-3); letter-spacing: 0.1em;
}
.lp-scenario-kind {
  font-family: var(--sans); font-size: 20px; font-weight: 600;
  letter-spacing: -0.02em; line-height: 1.2; margin-top: 4px;
}
.lp-scenario-tag { font-family: var(--sans); font-size: 12px; color: var(--ink-3); margin-top: 8px; display: block; }
.lp-scenario-col h4 {
  font-family: var(--mono); font-size: 11px; letter-spacing: 0.14em;
  text-transform: uppercase; color: var(--ink-3);
  margin: 0 0 12px; font-weight: 500;
}
.lp-scenario-col p { margin: 0; font-size: 15px; color: var(--ink-2); line-height: 1.55; }
.lp-before p { color: var(--ink-3); }
.lp-after { position: relative; padding-left: 20px; border-left: 2px solid var(--accent); }
.lp-after p { color: var(--ink); }
.lp-scenario-metric {
  display: inline-block; margin-top: 10px;
  font-family: var(--mono); font-size: 12px;
  color: var(--accent); padding: 3px 8px;
  background: var(--accent-soft); border-radius: 4px;
}

/* Capabilities */
.lp-capabilities {
  display: grid; grid-template-columns: 1fr; gap: 16px;
}
@media (min-width: 720px) { .lp-capabilities { grid-template-columns: 1fr 1fr; } }
.lp-cap {
  padding: 28px;
  background: var(--bg);
  border: 1px solid var(--line);
  border-radius: 16px;
  transition: all .2s ease;
}
.lp-section-alt .lp-cap { background: #ffffff; }
.lp-cap:hover { border-color: var(--ink-4); transform: translateY(-2px); }
.lp-cap-icon {
  width: 40px; height: 40px;
  border-radius: 10px;
  background: var(--accent-soft);
  color: var(--accent);
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 18px;
}
.lp-cap h3 {
  font-family: var(--sans); font-size: 18px; font-weight: 600;
  margin: 0 0 8px; letter-spacing: -0.01em; color: var(--ink);
}
.lp-cap p {
  font-size: 14px; color: var(--ink-2); margin: 0 0 14px; line-height: 1.55;
}
.lp-cap-examples {
  padding-top: 14px; border-top: 1px dashed var(--line);
  font-size: 13px; color: var(--ink-3); font-family: var(--mono);
}
.lp-cap-examples li { padding: 2px 0; list-style: none; }
.lp-cap-examples ul { margin: 0; padding: 0; }

/* Process */
.lp-process-steps {
  display: grid; grid-template-columns: 1fr; gap: 24px;
}
@media (min-width: 800px) { .lp-process-steps { grid-template-columns: repeat(3, 1fr); gap: 32px; } }
.lp-step {
  padding: 32px 28px;
  background: var(--bg-card);
  border: 1px solid var(--line);
  border-radius: 16px;
}
.lp-step-num {
  font-family: var(--mono); font-size: 13px; font-weight: 500;
  letter-spacing: 0.14em; color: var(--accent);
  margin-bottom: 20px; display: block;
}
.lp-step h3 {
  font-family: var(--sans); font-size: 18px; font-weight: 600;
  margin: 0 0 8px; letter-spacing: -0.01em; color: var(--ink);
}
.lp-step p { font-size: 14px; color: var(--ink-2); margin: 0 0 14px; line-height: 1.55; }
.lp-step-time { font-family: var(--mono); font-size: 12px; color: var(--ink-3); }

/* Testimonials */
.lp-quotes { display: grid; grid-template-columns: 1fr; gap: 24px; }
@media (min-width: 800px) { .lp-quotes { grid-template-columns: 1fr 1fr; } }
.lp-quote {
  padding: 32px;
  background: var(--bg-card);
  border: 1px solid var(--line);
  border-radius: 16px;
  display: flex; flex-direction: column; gap: 20px;
}
.lp-quote-mark {
  font-family: var(--sans); font-weight: 700;
  font-size: 56px; line-height: 0.5;
  color: var(--accent); height: 20px;
}
.lp-quote-body {
  font-family: var(--sans); font-size: 18px; font-weight: 500;
  line-height: 1.4; color: var(--ink); letter-spacing: -0.015em; flex-grow: 1;
}
.lp-quote-attr {
  display: flex; align-items: center; gap: 14px;
  padding-top: 18px; border-top: 1px solid var(--line);
}
.lp-quote-avatar {
  width: 42px; height: 42px; border-radius: 999px;
  background: var(--line);
  display: flex; align-items: center; justify-content: center;
  font-family: var(--sans); font-size: 16px; font-weight: 600;
  color: var(--ink-2); flex-shrink: 0;
}
.lp-quote-name { font-family: var(--sans); font-size: 14px; font-weight: 600; color: var(--ink); }
.lp-quote-meta { font-family: var(--sans); font-size: 13px; color: var(--ink-3); }

/* Pricing */
.lp-pricing {
  display: grid; grid-template-columns: 1fr; gap: 0;
  background: var(--bg-card);
  border: 1px solid var(--line);
  border-radius: 20px; overflow: hidden;
}
@media (min-width: 800px) { .lp-pricing { grid-template-columns: 1fr 1fr; } }
.lp-price-cell {
  padding: 40px; border-bottom: 1px solid var(--line);
}
@media (min-width: 800px) {
  .lp-price-cell { border-bottom: none; border-right: 1px solid var(--line); }
  .lp-price-cell:last-child { border-right: none; }
}
.lp-price-cell:last-child { border-bottom: none; }
.lp-price-label {
  font-family: var(--mono); font-size: 11px;
  letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--ink-3); margin-bottom: 16px;
}
.lp-price-amount {
  font-family: var(--sans); font-size: 48px; font-weight: 600;
  line-height: 1; letter-spacing: -0.035em; margin-bottom: 4px;
  color: var(--ink);
}
.lp-currency { font-size: 24px; color: var(--ink-3); vertical-align: top; margin-right: 4px; }
.lp-per { font-size: 16px; color: var(--ink-3); font-family: var(--sans); }
.lp-price-desc { font-family: var(--sans); font-size: 14px; color: var(--ink-2); margin: 12px 0 20px; }
.lp-price-list { list-style: none; padding: 0; margin: 0; font-size: 14px; color: var(--ink-2); }
.lp-price-list li { padding: 8px 0; display: flex; gap: 10px; align-items: flex-start; font-family: var(--sans); }
.lp-price-list li::before {
  content: ''; width: 6px; height: 6px; border-radius: 999px;
  background: var(--accent); margin-top: 8px; flex-shrink: 0;
}
.lp-price-note {
  text-align: center; font-family: var(--sans);
  font-size: 13px; color: var(--ink-3); margin-top: 20px;
}

/* FAQ */
.lp-faq { border-top: 1px solid var(--line); }
.lp-faq-item { border-bottom: 1px solid var(--line); padding: 24px 0; }
.lp-faq-q {
  display: flex; justify-content: space-between; gap: 24px;
  cursor: pointer;
  font-family: var(--sans); font-weight: 600;
  font-size: 17px; line-height: 1.35;
  color: var(--ink); letter-spacing: -0.015em;
  list-style: none;
}
.lp-faq-q::-webkit-details-marker { display: none; }
.lp-faq-toggle {
  font-family: var(--mono); font-size: 18px;
  color: var(--ink-3); flex-shrink: 0;
  transition: transform .2s ease;
}
details[open] .lp-faq-toggle { transform: rotate(45deg); }
.lp-faq-a {
  margin-top: 12px; font-family: var(--sans);
  font-size: 15px; color: var(--ink-2); line-height: 1.6; max-width: 68ch;
}

/* CTA / Form */
.lp-cta-section {
  padding: 120px 0;
  background: var(--ink);
  color: var(--bg);
  overflow: hidden;
}
.lp-h-cta { color: #f6f4ef; }
.lp-h-cta em { color: #d9c9a8; }
.lp-lede-cta { color: rgba(255,255,255,0.7); max-width: 52ch; }
.lp-cta-section .lp-eyebrow { color: rgba(255,255,255,0.5); }
.lp-cta-grid {
  display: grid; grid-template-columns: 1fr; gap: 56px; align-items: start;
}
@media (min-width: 880px) {
  .lp-cta-grid { grid-template-columns: 1fr 1fr; gap: 80px; }
}
.lp-cta-form {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 20px; padding: 36px;
}
.lp-form-row { margin-bottom: 18px; }
.lp-form-row label {
  display: block;
  font-family: var(--mono); font-size: 11px;
  letter-spacing: 0.14em; text-transform: uppercase;
  color: rgba(255,255,255,0.5); margin-bottom: 8px;
}
.lp-optional {
  font-family: var(--sans);
  color: rgba(255,255,255,0.3);
  text-transform: none; letter-spacing: 0;
}
.lp-form-row input,
.lp-form-row textarea {
  width: 100%; padding: 14px 16px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 10px; color: #fff;
  font-family: var(--sans); font-size: 15px;
  transition: border-color .15s;
}
.lp-form-row input:focus,
.lp-form-row textarea:focus {
  outline: none;
  border-color: rgba(255,255,255,0.4);
  background: rgba(255,255,255,0.08);
}
.lp-form-row input::placeholder,
.lp-form-row textarea::placeholder { color: rgba(255,255,255,0.3); }
.lp-form-row textarea { min-height: 80px; resize: vertical; }
.lp-form-submit {
  width: 100%;
  background: #f6f4ef; color: var(--ink);
  border: none; padding: 16px;
  border-radius: 12px; font-weight: 600; font-size: 15px;
  font-family: var(--sans);
  transition: all .15s; margin-top: 8px; cursor: pointer;
}
.lp-form-submit:hover:not(:disabled) { transform: translateY(-1px); background: #fff; }
.lp-form-submit:disabled { opacity: 0.6; cursor: not-allowed; }
.lp-form-foot {
  text-align: center; font-family: var(--sans);
  font-size: 12px; color: rgba(255,255,255,0.4); margin-top: 14px;
}
.lp-cta-alt {
  display: flex; align-items: center; gap: 10px;
  margin-top: 16px; padding: 14px 18px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 12px;
  color: rgba(255,255,255,0.8); font-family: var(--sans); font-size: 14px;
  text-decoration: none;
  transition: background .15s;
}
.lp-cta-alt:hover { background: rgba(255,255,255,0.08); }
.lp-cta-expectations {
  list-style: none; padding: 0; margin: 32px 0 0;
  font-family: var(--sans); font-size: 14px; color: rgba(255,255,255,0.7);
}
.lp-cta-expectations li {
  padding: 14px 0;
  border-top: 1px solid rgba(255,255,255,0.08);
  display: flex; gap: 14px;
}
.lp-cta-expectations li:first-child { border-top: none; padding-top: 0; }
.lp-num {
  font-family: var(--mono); color: rgba(255,255,255,0.5);
  font-size: 12px; letter-spacing: 0.1em;
  flex-shrink: 0; width: 24px; padding-top: 3px;
}

/* Success */
.lp-success-state {
  text-align: center; padding: 40px 20px;
}
.lp-success-check {
  width: 64px; height: 64px; margin: 0 auto 20px;
  border-radius: 999px;
  background: rgba(217, 201, 168, 0.15);
  display: flex; align-items: center; justify-content: center;
  color: #d9c9a8;
}
.lp-success-title {
  font-family: var(--sans); font-size: 24px; color: #fff;
  margin: 0 0 8px; font-weight: 600; letter-spacing: -0.02em;
}
.lp-success-sub { color: rgba(255,255,255,0.6); margin: 0; font-size: 15px; font-family: var(--sans); }

/* Footer */
.lp-footer {
  padding: 48px 0;
  border-top: 1px solid var(--line);
  font-family: var(--sans); font-size: 13px; color: var(--ink-3);
  background: var(--bg);
}
.lp-footer-inner {
  display: flex; justify-content: space-between; align-items: center;
  flex-wrap: wrap; gap: 16px;
}
.lp-footer-links { display: flex; gap: 20px; }

/* ── Chat demo widget ── */
.lp-chat-widget {
  position: fixed;
  bottom: 28px;
  right: 28px;
  z-index: 200;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}
@media (max-width: 480px) {
  .lp-chat-widget { bottom: 12px; right: 12px; left: 12px; align-items: flex-end; }
}

.lp-chat-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0;
  padding: 0 22px;
  height: 52px;
  background: var(--accent);
  color: #fff;
  border: none;
  border-radius: 999px;
  font-family: var(--sans);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 8px 28px -8px rgba(10,59,92,0.55);
  transition: transform .18s ease, box-shadow .18s ease;
  overflow: hidden;
}
.lp-chat-toggle:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 36px -10px rgba(10,59,92,0.6);
}
.lp-chat-toggle-open { padding: 0; width: 52px; }
.lp-chat-toggle-inner {
  display: inline-flex; align-items: center; gap: 10px;
}

.lp-chat-panel {
  width: 360px;
  max-height: 520px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 20px;
  box-shadow: 0 24px 64px -16px rgba(19,17,14,0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
@media (max-width: 480px) {
  .lp-chat-panel {
    width: 100%;
    max-height: 60vh;
    height: 60vh;
  }
}

.lp-chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--line);
  background: var(--bg);
  flex-shrink: 0;
}
.lp-chat-header-info { display: flex; align-items: center; gap: 12px; }
.lp-chat-avatar {
  width: 36px; height: 36px; border-radius: 999px;
  background: var(--accent);
  color: #fff; font-family: var(--mono); font-size: 11px; font-weight: 500;
  display: flex; align-items: center; justify-content: center;
  letter-spacing: 0.05em;
  flex-shrink: 0;
}
.lp-chat-title {
  font-family: var(--sans); font-size: 14px; font-weight: 600;
  color: var(--ink); letter-spacing: -0.01em;
}
.lp-chat-subtitle {
  display: flex; align-items: center; gap: 6px;
  font-family: var(--mono); font-size: 11px; color: var(--ink-3);
  margin-top: 2px;
}
.lp-chat-close-btn {
  width: 32px; height: 32px; border-radius: 8px;
  border: 1px solid var(--line);
  background: transparent; color: var(--ink-3);
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  transition: background .15s;
}
.lp-chat-close-btn:hover { background: var(--line); color: var(--ink); }

.lp-chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  scroll-behavior: smooth;
}

.lp-chat-msg { display: flex; }
.lp-chat-msg-user { justify-content: flex-end; }
.lp-chat-msg-ai { justify-content: flex-start; }

.lp-chat-bubble {
  max-width: 82%;
  padding: 11px 15px;
  border-radius: 16px;
  font-family: var(--sans);
  font-size: 14px;
  line-height: 1.5;
}
.lp-chat-msg-ai .lp-chat-bubble {
  background: #f4f4f4;
  color: var(--ink);
  border-bottom-left-radius: 4px;
}
.lp-chat-msg-user .lp-chat-bubble {
  background: var(--accent);
  color: #fff;
  border-bottom-right-radius: 4px;
}

/* Typing indicator */
.lp-chat-typing {
  display: flex; align-items: center; gap: 5px;
  padding: 14px 18px;
}
.lp-chat-typing span {
  width: 7px; height: 7px; border-radius: 999px;
  background: var(--ink-4);
  animation: lp-bounce 1.2s ease-in-out infinite;
}
.lp-chat-typing span:nth-child(2) { animation-delay: .2s; }
.lp-chat-typing span:nth-child(3) { animation-delay: .4s; }
@keyframes lp-bounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-6px); }
}

/* Suggestions */
.lp-chat-suggestions {
  display: flex; flex-wrap: wrap; gap: 6px;
  padding: 6px 16px 2px;
  flex-shrink: 0;
}
.lp-chat-chip {
  padding: 6px 12px;
  background: var(--accent-soft);
  color: var(--accent);
  border: 1px solid rgba(10,59,92,0.15);
  border-radius: 999px;
  font-family: var(--sans); font-size: 12px; font-weight: 500;
  cursor: pointer; white-space: nowrap;
  transition: background .15s, border-color .15s;
}
.lp-chat-chip:hover {
  background: #ccdde8;
  border-color: rgba(10,59,92,0.3);
}

.lp-chat-input-row {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid var(--line);
  flex-shrink: 0;
}
.lp-chat-input {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid var(--line-2);
  border-radius: 10px;
  font-family: var(--sans); font-size: 14px;
  color: var(--ink);
  background: var(--bg);
  outline: none;
  transition: border-color .15s;
}
.lp-chat-input:focus { border-color: var(--accent); }
.lp-chat-input::placeholder { color: var(--ink-4); }
.lp-chat-input:disabled { opacity: 0.6; }
.lp-chat-send {
  width: 38px; height: 38px; border-radius: 10px;
  background: var(--accent); color: #fff;
  border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all .15s; flex-shrink: 0;
}
.lp-chat-send:hover:not(:disabled) { transform: scale(1.05); }
.lp-chat-send:disabled { opacity: 0.4; cursor: not-allowed; }
.lp-chat-footer-note {
  text-align: center;
  font-family: var(--mono); font-size: 10px;
  color: var(--ink-4); letter-spacing: 0.08em;
  padding: 8px 16px 10px;
  flex-shrink: 0;
}

/* Chat panel slide transition */
.lp-chat-slide-enter-active { animation: lp-chat-in .22s cubic-bezier(0.34, 1.56, 0.64, 1); }
.lp-chat-slide-leave-active { animation: lp-chat-out .18s ease; }
@keyframes lp-chat-in {
  from { opacity: 0; transform: translateY(16px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes lp-chat-out {
  from { opacity: 1; transform: translateY(0) scale(1); }
  to   { opacity: 0; transform: translateY(12px) scale(0.97); }
}

/* Icon swap transition */
.lp-icon-swap-enter-active, .lp-icon-swap-leave-active { transition: opacity .15s, transform .15s; }
.lp-icon-swap-enter-from { opacity: 0; transform: scale(0.7); }
.lp-icon-swap-leave-to { opacity: 0; transform: scale(0.7); }
</style>
